import {AxiosResponse} from "axios";
import React from "react";
import {NavLink} from "react-router-dom";
import DropDown from "../../../../common/components/shared/dropDown/dropDown";
import {HttpError} from "../../../../common/components/shared/httpError/httpError";
import Loader from "../../../../common/components/shared/loader/loader";
import ReloadButton from "../../../../common/components/shared/reloadButton/reloadButton";
import Table, {TableActionFunction} from "../../../../common/components/shared/table/table";
import TextInput from "../../../../common/components/shared/textInput/textInput";
import {HttpErrorModel} from "../../../../common/models/httpError";
import {Api} from "../../../../helpers/api";
import {DateUtils} from "../../../../helpers/dateUtils";
import {routingAddresses} from "../../../../helpers/routing";
import {ValidatedValue} from "../../../../helpers/validatedValue";
import i18n from "../../../../i18n/i18n";
import {LoadingType, LoadingTypeFirstLetter, DocumentsDirectionTypes} from "../../../models/enums";
import {ManifestModel} from "../../../models/manifest";
import {ManifestService} from "../../../services/manifestService";
import "../manifests.scss";
import "../../documents.scss";
import {ShipownerModel} from "../../../../dictionaries/models/shipowners";
import {ShipownerService} from "../../../../dictionaries/services/shipownerService";
import {DropDownUtils} from "../../../../helpers/dropDownUtils";
import {SortDirection} from "../../../../common/models/table";
import NumberDropDownItems from "../../../../helpers/numberDropDownItems";
import {storesContext} from "../../../../common/stores/stores";
import {IPaginationStore} from "../../../../common/stores/paginationStore";
import SearchButton from "../../../../common/components/shared/searchButton/searchButton";
import DocumentsDirectionTypeDropdownItems from "../../../../helpers/documentsDirectionTypeDropDownItems";

interface ManifestListState {
    manifestData: ManifestModel[] | undefined | null;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    currentPage: number;
    isTableLoader: boolean;
    versionFilter: ValidatedValue<string>;
    typeFilter: ValidatedValue<DocumentsDirectionTypes | "">;
    shipownerFilter: ValidatedValue<string>;
    voyageNoFilter: ValidatedValue<string>;
    shipowners: ShipownerModel[];
    orderBy?: string;
    sortDirection?: SortDirection;
    isFilterWaiting: boolean;
    shipownersResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
}

export default class ManifestList extends React.Component<any, ManifestListState> {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        this.state = {
            manifestData: undefined,
            response: undefined,
            currentPage: 0,
            isTableLoader: false,
            versionFilter: new ValidatedValue(""),
            typeFilter: new ValidatedValue(""),
            shipownerFilter: new ValidatedValue(""),
            voyageNoFilter: new ValidatedValue(""),
            shipowners: [],
            orderBy: undefined,
            sortDirection: undefined,
            isFilterWaiting: false,
            shipownersResponse: undefined
        };
    }

    get manifestData () {
        return this.state.manifestData;
    }

    public async componentDidMount() {
        await this.getShipowners();

        await this.getAllManifests();
    }

    public render(): JSX.Element {
        const properties = ["version", "directionType", "shipownerName", "ferryName", "voyageNo", "bookingNo", "arrivalDepartureTime", "loadingType", "cargoDesc"];
        const headers = properties.map(key => i18n.t("documents.manifests.headers." + key));
        const actions = this.renderActions;

        return (
            <div className="manifests">
                {this.renderWrapperContent(headers, properties, "manifests", actions, this.renderCell)}
            </div>
        );
    }

    private renderHeader() {
        return (
            <div className="header">
                <div className="filters-content">
                    {this.renderFilters()}
                </div>
                <div className="actions">
                    <SearchButton
                        isWaiting={this.state.isFilterWaiting}
                        onClick={async () => {
                            this.setState({isFilterWaiting: true});
                            await this.getAllManifests();
                            this.setState({isFilterWaiting: false});
                        }}
                    />
                    <ReloadButton
                        reloadFunc={this.reload.bind(this)}
                        testId="manifest-list-reload-btn"
                    />
                </div>
            </div>
        );
    }

    private renderWrapperContent(headers: string[], properties: string[], testId: string, actions?: TableActionFunction, renderCell?: (row: any, property: string) => JSX.Element) {
        if (!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={i18n.t("documents.retrievalErrorSuffix")}
                />
            );
        } else {
            if (this.manifestData === undefined) {
                return <Loader/>;
            } else {
                return (
                    <>
                        {this.renderHeader()}
                        <div className="manifests-content">
                            {this.manifestData && this.manifestData!.length && this.manifestData!.length > 0 ? (
                                <Table headers={headers} rows={this.state.manifestData!} properties={properties}
                                    testId={`${testId}-table`}
                                    actions={actions ? actions.bind(this) : actions}
                                    renderCell={renderCell}
                                    pageable={true}
                                    currentPage={this.state.currentPage}
                                    isOldPagination={false}
                                    isTableLoader={this.state.isTableLoader}
                                    onPageChange={this.getAllManifests.bind(this)}
                                />
                            ) : (
                                <Table
                                    headers={headers}
                                    rows={[]}
                                    properties={properties}
                                    testId={`${testId}-table`}
                                    numberOfElements={0}
                                    currentPage={0}
                                />
                            )}
                        </div>
                    </>
                );
            }
        }
    }

    private renderFilters() {
        return <div className="filters">
            <DropDown
                label={i18n.t("documents.manifests.filters.version")}
                validatedValue={this.state.versionFilter}
                items={NumberDropDownItems.getNumberDropDownItems(100)}
                testId="filters-version"
                withEmptyValue
                withoutSort
            />
            <DropDown
                label={i18n.t("documents.manifests.filters.type")}
                validatedValue={this.state.typeFilter}
                items={DocumentsDirectionTypeDropdownItems.getDocumentsDirectionTypeDropdownItems()}
                testId="filters-type"
                withEmptyValue
            />
            <DropDown
                label={i18n.t("documents.manifests.filters.shipowner")}
                validatedValue={this.state.shipownerFilter}
                items={this.shipownersToDropDownMap}
                testId="filters-shipowner"
                withEmptyValue
                error={!Api.isSuccess(this.state.shipownersResponse) ? i18n.t("errorMessages.shipownersErrorSuffix") : ""}
                reloadFunc={() => this.getShipowners()}
                isNoRequired
            />
            <TextInput
                label={i18n.t("documents.manifests.filters.voyageNo")}
                validatedValue={this.state.voyageNoFilter}
                testId="filters-voyage-no"
                offAutocomplete
            />
        </div>;
    }

    private renderActions(element: ManifestModel) {
        // @ts-ignore
        const elementId = element.id;
        //TODO Add the missing actions in the next step

        return (
            <>
                {elementId && (
                    <>
                        <NavLink
                            to={`${routingAddresses.documents.manifests.details}/${elementId}`}
                            className="preview-button"
                            data-testid={`preview-${elementId}`}
                        >
                            {i18n.t("documents.manifests.preview")}
                        </NavLink>
                    </>
                )}
            </>
        );
    }

    private renderCell(row: any, property: string): JSX.Element {
        if(property === "directionType") {
            return <>{i18n.t(`directionType.${row[property]}`)}</>;
        } else if(property === "arrivalDepartureTime") {
            return <>{DateUtils.getFormattedDateWithTime(row[property])}</>;
        } else if(property === "loadingType") {
            return <>{LoadingTypeFirstLetter[row[property] as LoadingType]}</>;
        }
        return row[property];
    }

    private async getAllManifests() {
        const paginationStore = this.context.paginationStore as IPaginationStore;

        this.setState({
            isTableLoader: true,
            manifestData: undefined
        });

        const filters = {
            type: this.state.typeFilter.value,
            shipownerId: this.state.shipownerFilter.value,
            voyageNo: this.state.voyageNoFilter.value.trim()
        };

        const response = await ManifestService.getAll(
            this.state.versionFilter.value,
            filters,
            {
                offset: paginationStore.offset,
                size: paginationStore.size
            },
            {
                orderBy: this.state.orderBy,
                sortDirection: this.state.sortDirection
            }
        );

        this.setState({
            manifestData: response?.data?.data || null,
            response,
            currentPage: response?.data?.currentPage,
            isTableLoader: false
        });

        paginationStore.setNumberOfElements(response?.data?.numberOfElements);
        paginationStore.setNumberOfPages(response?.data?.numberOfPages);
    }

    private async getShipowners() {
        const shipownersResponse = await ShipownerService.getAll();

        this.setState({
            shipowners: (shipownersResponse)?.data?.data || [],
            shipownersResponse
        });
    }

    private async reload() {
        this.setState({manifestData: undefined});
        await this.getAllManifests();
    }

    public get shipownersToDropDownMap() {
        return DropDownUtils.listToDropDownMap(this.state.shipowners, "entityId");
    }
}
